// Public browsers must not depend on a direct route to Yandex Functions.
// Cloudflare terminates the browser request and forwards it to the fixed API.
const DEFAULT_API_URL = "https://api.cheatchecker.ru";

export const apiUrl = String(import.meta.env.VITE_CCBE_API_URL || DEFAULT_API_URL).replace(/\/+$/, "");

const API_REQUEST_TIMEOUT_MS = 10_000;
const EVIDENCE_UPLOAD_TIMEOUT_MS = 45_000;

export type SiteApiErrorStage = "fetch" | "response_json" | "result";

export class SiteApiError extends Error {
  constructor(message: string, readonly stage: SiteApiErrorStage, readonly apiStatus?: string) {
    super(message);
    this.name = "SiteApiError";
  }
}

export type BackendHealth = {
  ok: boolean;
  status: string;
  backendVersion?: string;
};

export type LoaderRelease = {
  ok: boolean;
  status: string;
  backendVersion?: string;
  updateAvailable?: boolean;
  provider?: string;
  sourceProvider?: string;
  version?: string;
  fileName?: string;
  sha256?: string;
  publicUrl?: string;
  versionTextPath?: string;
  downloadUrl?: string;
};

export type SiteUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  projectId?: string;
  authProvider?: string;
  emailVerified?: boolean;
  steamId64?: string;
  language?: "ru" | "en";
  developer?: boolean;
};

export type SiteDeveloperDashboard = {
  ok: boolean;
  status: "ok";
  limit: number;
  data: {
    purchases: Array<{ id: string; status: string; provider: string; amount: number | null; currency: string | null; ownerEmail: string; createdAt: string; paidAt: string }>;
    users: Array<{ id: string; email: string; authProvider: string; emailVerified: boolean; accessStatus: string; createdAt: string; lastLoginAt: string }>;
    logins: Array<{ id: string; email: string; provider: string; createdAt: string }>;
    projects?: Array<{
      name: string;
      accessStatus: string;
      accessExpiresAt?: string | null;
      purchasedAt?: string | null;
      purchaseProvider?: string | null;
      purchaseAmount?: number | null;
      purchaseCurrency?: string | null;
    }>;
  };
  privacy: { emails: "developer_only" | "masked"; tokens: "never_returned"; keys: "never_returned" };
};

export type SiteDeveloperPromoCode = {
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  validFrom?: string | null;
  validTo?: string | null;
  redemptionLimit?: number | null;
  redemptions: number;
  active: boolean;
  notes?: string;
};

export type SitePromoPreflight = {
  ok: true;
  status: "ok";
  promo: { code: string };
  amount: number;
  currency: "RUB" | "USD";
  settlementBaseAmount?: number;
  settlementAmount?: number;
  settlementCurrency?: "RUB";
  requiresRubleSettlement?: boolean;
};

export type SiteAuthResult = {
  ok: true;
  status: "ok" | string;
  siteToken: string;
  user: SiteUser;
};

export type SiteTwoFactorRequired = {
  ok: false;
  status: "two_factor_required";
};

export type SiteRegistrationPending = {
  ok: boolean;
  status: string;
  email: string;
};

export type SiteSessionResult = {
  ok: boolean;
  status: string;
  user: SiteUser;
};

export type SiteOAuthStartResult = {
  ok: boolean;
  status: string;
  provider: "google" | "yandex";
  authUrl: string;
  state: string;
};

export type SiteSteamStartResult = {
  ok: boolean;
  status: string;
  provider: "steam";
  authUrl: string;
  state: string;
  requiresEmailAfterLogin?: boolean;
};

export type SiteSteamCompleteSuccess = SiteAuthResult & {
  provider: "steam";
  emailRequired: boolean;
};

export type SiteSteamTwoFactorRequired = {
  ok: false;
  status: "two_factor_required" | "two_factor_setup_required";
  twoFactorLoginSession: string;
};

export type SiteSteamCompleteResult = SiteSteamCompleteSuccess | SiteSteamTwoFactorRequired;

export type SiteDashboard = {
  ok: boolean;
  status: string;
  account: SiteUser;
  hasProject: boolean;
  project: null | {
    id: string;
    displayName: string;
    game: string;
    ownerDisplayName: string;
    status: string;
    licensePlan: string;
    licenseSource: string;
    licenseStartedAt: string;
    licenseExpiresAt: string;
    subscription: SiteSubscriptionLifecycle;
    maxModerators?: number;
    profileRequired: boolean;
  };
  ownerKey: null | {
    fingerprint: string;
    status: string;
    type: string;
    uses: number;
    maxUses: number;
    displayPrefix: string;
    key: string;
    expiresAt: string;
    bindsHwid: boolean;
    hwidBound: boolean;
  };
  stats: {
    totalChecks: number;
    totalBans: number;
    todayChecks: number;
    todayBans: number;
    averageDuration: string;
    activeSessions: number;
    completedSessions: number;
    moderators: number;
  };
  moderators: Array<{
    id: string;
    displayName: string;
    steamId64: string;
    status: string;
    createdAt: string;
  }>;
  activeSessions: Array<Record<string, string | string[]>>;
  completedSessions: Array<Record<string, string | string[]>>;
};

export type SiteModeratorDashboard = {
  ok: boolean;
  status: string;
  account: SiteUser;
  subscription?: SiteSubscriptionLifecycle;
  moderator: { id: string; displayName: string; steamId64: string; status: string };
  project: { id: string; displayName: string; game: string; status: string; subscription?: SiteSubscriptionLifecycle };
  stats: Omit<SiteDashboard["stats"], "moderators">;
  activeSessions: Array<Record<string, string | string[]>>;
  completedSessions: Array<Record<string, string | string[]>>;
};

export type SiteModeratorCheckResult = {
  ok: boolean;
  status: string;
  sessionId: string;
  mode?: "steam" | "key";
  suspectSteamId64?: string;
  suspectName?: string;
  oneTimeKey?: string;
  expiresAt?: string;
  maxUses?: number;
};

export type EvidenceLocation = "quick_scan" | "recent_files" | "registry" | "processes" | "recycle_bin" | "downloads" | "browser_history" | "purchase" | "other";

export type SiteCheckFinishPayload =
  | { sessionId: string; verdict: "clean" }
  | {
    sessionId: string;
    verdict: "ban";
    reason?: string;
    banDuration?: "two_weeks" | "one_month" | "three_months" | "six_months" | "one_year" | "forever" | "custom";
    banDays?: number;
    evidenceLocation: EvidenceLocation;
    evidenceComment?: string;
  };

export type SiteCheckEvidenceUpload = {
  ok: true;
  status: "ok";
  uploadId: string;
  chunkSize: number;
  chunkCount: number;
  duplicate?: boolean;
  completed?: boolean;
  finalizing?: boolean;
};

export type SiteCheckHistoryItem = {
  id: string;
  checkNumber: string;
  suspectSteamId64: string;
  moderatorName: string;
  startedAt: string;
  status: "completed" | "running" | "cancelled" | "expired";
  verdict?: "clean" | "ban" | string;
  verdictLabel?: string;
};

export type SiteCheckHistory = {
  ok: true;
  status: "ok";
  checks: SiteCheckHistoryItem[];
  hasMore: boolean;
  nextCursor?: string;
};

export type SiteCheckDetails = {
  ok: true;
  status: "ok";
  check: SiteCheckHistoryItem & {
    status: string;
    moderatorId: string;
    suspectName?: string;
    suspectSteamIds?: string[];
    completedAt?: string;
    duration?: string;
    reason?: string;
    evidenceLocation?: string;
    evidenceComment?: string;
    evidenceFiles?: SiteCheckEvidenceFile[];
  };
  events: Array<{ type: string; tab: string; action?: string; actorRole?: string; verdict?: string; at: string }>;
};

export type SiteCheckEvidenceFile = {
  id: string;
  kind: "quick_scan_pdf" | "verdict_screenshot";
  mimeType: string;
  fileName: string;
  byteLength: number;
  sha256: string;
  uploadedByRole: string;
  uploadedAt: string;
};

export type SiteCheckPriorHistory = {
  ok: true;
  status: "ok";
  prior_checks: Array<{
    sessionId: string;
    date: string;
    project: string;
    duration: string;
    verdict: string;
    verdictLabel: string;
    comment: string;
    evidenceFiles: SiteCheckEvidenceFile[];
  }>;
};

export type SiteOwnerModeratorStats = {
  ok: true;
  status: "ok";
  moderator: { id: string; displayName: string; steamId64: string; status: string };
  stats: {
    totalChecks: number;
    totalBans: number;
    todayChecks: number;
    todayBans: number;
    activeSessions: number;
    completedSessions: number;
    averageDuration: string;
  };
  recentChecks: Array<Record<string, string | string[]>>;
};

export type SiteSecuritySettings = {
  ok: true;
  status: "ok";
  twoFactor: { enabled: boolean; method?: "totp" };
};

export type SiteTwoFactorSetup = {
  ok: true;
  status: "ok";
  otpauthUri: string;
  qrCodeDataUrl: string;
};

export type SitePurchaseResult = {
  ok: boolean;
  status: string;
  projectId?: string;
  key?: string;
  keyDisplayPrefix?: string;
  dashboard?: SiteDashboard;
};

export type SitePaymentConfig = {
  ok: boolean;
  status: string;
  provider: "robokassa";
  enabled: boolean;
  testMode: boolean;
  amount: number;
  listAmount?: number;
  defaultPlanId: SubscriptionPlanId;
  activePlanId: SubscriptionPlanId;
  plans: SiteSubscriptionPlan[];
  currency: "RUB" | "USD";
  paymentCurrency?: "RUB";
  pricing?: {
    displayCurrency: "RUB" | "USD";
    settlementCurrency: "RUB";
  };
  requiresRubleSettlement?: boolean;
  licenseDays: number;
  paymentMethods: Array<"sbp" | "bank_card">;
  sellerName: string;
  offerUrl: string;
  privacyUrl: string;
  legalReady: boolean;
  emailVerified: boolean;
  hasProject: boolean;
  subscription: SiteSubscriptionLifecycle;
  access?: {
    status: "payment_required" | "provisioning_required" | "paid" | "active";
    canCreateProject: boolean;
    hasProject: boolean;
    licenseDays?: number;
    expiresAt?: string;
  };
};

export type SiteSubscriptionLifecycle = {
  status: "active" | "expiring_soon" | "grace" | "frozen" | "none";
  mode: "purchase" | "renewal";
  licenseExpiresAt: string | null;
  graceExpiresAt: string | null;
  daysRemaining: number | null;
  actionsAllowed: boolean;
  canRenew: boolean;
};

export type SubscriptionPlanId = "monthly" | "quarterly" | "annual";

export type SiteSubscriptionPlan = {
  id: SubscriptionPlanId;
  months: 1 | 3 | 12;
  amount: number;
  currency?: "RUB" | "USD";
  listAmount?: number;
  settlementAmount?: number;
  settlementListAmount?: number;
  settlementCurrency?: "RUB";
  compareAt?: number;
  monthlyEquivalent: number;
  savings: number;
  discountPercent: number;
  maxModerators: number;
  labels: { ru: string; en: string };
  recommended: boolean;
  active: boolean;
};

export type SitePaymentOrder = {
  ok: boolean;
  status: "not_started" | "pending" | "paid" | "paid_conflict" | string;
  provider?: "robokassa";
  invoiceId?: string;
  paymentUrl?: string;
  paymentForm?: {
    action: string;
    fields: Record<string, string>;
  } | null;
  amount?: number;
  currency?: "RUB" | "USD";
  paymentCurrency?: "RUB";
  settlementAmount?: number;
  settlementCurrency?: "RUB";
  displayPricing?: {
    amount?: number;
    listAmount?: number;
    currency?: "RUB" | "USD";
  };
  requiresRubleSettlement?: boolean;
  licenseDays?: number;
  expiresAt?: string;
  purpose?: "purchase" | "renewal";
  renewalBaseAt?: string;
  licenseExpiresAt?: string;
  graceExpiresAt?: string;
  months?: 1 | 3 | 12;
  subscription?: SiteSubscriptionLifecycle;
};

export type SiteDownloadStatsResult = {
  ok: boolean;
  status: string;
  downloads: number;
};

let downloadStatsRequest: Promise<SiteDownloadStatsResult> | null = null;

async function postOperation<T>(operation: string, payload: Record<string, unknown> = {}, options: { noStore?: boolean; allowPending?: boolean; timeoutMs?: number } = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), options.timeoutMs ?? API_REQUEST_TIMEOUT_MS);

  try {
    let response: Response;
    try {
      response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(options.noStore ? { "Cache-Control": "no-store" } : {}),
        },
        ...(options.noStore ? { cache: "no-store" as RequestCache } : {}),
        body: JSON.stringify({ operation, ...payload }),
        signal: controller.signal,
      });
    } catch {
      if (controller.signal.aborted) {
        throw new SiteApiError("request_timeout", "fetch");
      }
      throw new SiteApiError("network_error", "fetch");
    }

    const data = await response.json().catch(() => null);
    if (data === null) {
      throw new SiteApiError(`http_${response.status}`, "response_json");
    }
    if (!response.ok || (data.ok === false && !options.allowPending)) {
      const apiStatus = typeof data.status === "string" && /^[a-z][a-z0-9_]{0,63}$/.test(data.status) ? data.status : undefined;
      throw new SiteApiError(apiStatus || `http_${response.status}`, "result", apiStatus);
    }
    return data as T;
  } catch (error) {
    if (error instanceof SiteApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new SiteApiError(error.message, "result");
    }
    throw new SiteApiError("network_error", "result");
  } finally {
    window.clearTimeout(timeout);
  }
}

export function getHealth() {
  return postOperation<BackendHealth>("health");
}

export function getSiteDownloadRelease() {
  return postOperation<LoaderRelease>("siteDownloadRelease");
}

export function siteRegister(payload: { email: string; password: string; language: "ru" | "en" }) {
  return postOperation<SiteRegistrationPending>("siteRegister", payload);
}

export function siteLogin(payload: { email: string; password: string; language: "ru" | "en"; twoFactorCode?: string }) {
  return postOperation<SiteAuthResult | SiteTwoFactorRequired>("siteLogin", payload, { allowPending: true, noStore: true });
}

export function siteSession(siteToken: string, language: "ru" | "en") {
  return postOperation<SiteSessionResult>("siteSession", { siteToken, language });
}

export function siteLogout(siteToken: string) {
  return postOperation<{ ok: boolean; status: string }>("siteLogout", { siteToken });
}

export function siteForgotPassword(email: string, language: "ru" | "en") {
  return postOperation<{ ok: boolean; status: string }>("siteForgotPassword", { email, language });
}

export type SitePasswordResetExchange = {
  ok: true;
  status: "password_reset_session_issued";
  resetSession: string;
  expiresAt: string;
};

export function siteExchangePasswordReset(resetToken: string) {
  return postOperation<SitePasswordResetExchange>("siteExchangePasswordReset", { resetToken }, { noStore: true });
}

export function siteResetPassword(payload: { resetSession: string; password: string }) {
  return postOperation<{ ok: true; status: "password_reset_complete" }>("siteResetPassword", payload, { noStore: true });
}

export function siteDownloadStats() {
  if (!downloadStatsRequest) {
    const request = postOperation<SiteDownloadStatsResult>("siteDownloadStats");
    downloadStatsRequest = request;
    void request.catch(() => {
      if (downloadStatsRequest === request) {
        downloadStatsRequest = null;
      }
    });
  }
  return downloadStatsRequest;
}

export function siteResendVerification(siteToken: string, language: "ru" | "en") {
  return postOperation<{ ok: boolean; status: string; user: SiteUser }>("siteResendVerification", { siteToken, language });
}

export function siteDashboard(siteToken: string) {
  return postOperation<SiteDashboard>("siteDashboard", { siteToken });
}

export function siteDeveloperDashboard(siteToken: string, limit = 50) {
  return postOperation<SiteDeveloperDashboard>("siteDeveloperDashboard", { siteToken, limit });
}

export function siteDeveloperRequestVerification(siteToken: string, language: "ru" | "en") {
  return postOperation<{ ok: boolean; status: "developer_verification_sent"; expiresAt: string }>("siteDeveloperRequestVerification", { siteToken, language });
}

export function siteDeveloperVerify(siteToken: string, code: string) {
  return postOperation<{ ok: boolean; status: "developer_verified"; expiresAt: string }>("siteDeveloperVerify", { siteToken, code });
}

export function siteDeveloperListPromoCodes(siteToken: string) {
  return postOperation<{ ok: boolean; status: string; promoCodes: SiteDeveloperPromoCode[] }>("siteDeveloperListPromoCodes", { siteToken });
}

export function siteDeveloperCreatePromoCode(siteToken: string, payload: {
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  validFrom?: string;
  validTo?: string;
  redemptionLimit?: number;
  active?: boolean;
  notes?: string;
}) {
  return postOperation<{ ok: boolean; status: string; promoCode?: SiteDeveloperPromoCode }>("siteDeveloperCreatePromoCode", { siteToken, ...payload });
}

export function siteDeveloperUpdatePromoCode(siteToken: string, code: string, payload: { active?: boolean; notes?: string }) {
  return postOperation<{ ok: boolean; status: string; promoCode?: SiteDeveloperPromoCode }>("siteDeveloperUpdatePromoCode", { siteToken, code, ...payload });
}

export function sitePromoPreflight(siteToken: string, code: string, planId?: SubscriptionPlanId, language?: "ru" | "en") {
  return postOperation<SitePromoPreflight>("sitePromoPreflight", { siteToken, code, ...(planId ? { planId } : {}), ...(language ? { language } : {}) });
}

export function siteModeratorDashboard(siteToken: string) {
  return postOperation<SiteModeratorDashboard>("siteModeratorDashboard", { siteToken });
}

export function siteModeratorCreateCheck(siteToken: string, payload: { mode?: "steam" | "key"; suspectSteamId64?: string; suspectName?: string }) {
  return postOperation<SiteModeratorCheckResult>("siteModeratorCreateCheck", { siteToken, ...payload });
}

export function siteModeratorFinishCheck(siteToken: string, payload: {
  sessionId: string;
  verdict: "clean" | "ban";
  reason?: string;
  banDuration?: "two_weeks" | "one_month" | "three_months" | "six_months" | "one_year" | "forever" | "custom";
  banDays?: number;
} | SiteCheckFinishPayload) {
  return postOperation<SiteModeratorCheckResult>("siteModeratorFinishCheck", { siteToken, ...payload });
}

export function siteModeratorCancelCheck(siteToken: string, sessionId: string) {
  return postOperation<SiteModeratorCheckResult>("siteModeratorCancelCheck", { siteToken, sessionId });
}

export function siteOwnerCreateCheck(siteToken: string, payload: { mode: "key"; suspectName?: string } | { mode: "steam"; suspectSteamId64: string; suspectName?: string }) {
  return postOperation<SiteModeratorCheckResult>("siteOwnerCreateCheck", { siteToken, ...payload });
}

export function siteOwnerFinishCheck(siteToken: string, payload: Parameters<typeof siteModeratorFinishCheck>[1]) {
  return postOperation<SiteModeratorCheckResult>("siteOwnerFinishCheck", { siteToken, ...payload });
}

export function siteOwnerCancelCheck(siteToken: string, sessionId: string) {
  return postOperation<SiteModeratorCheckResult>("siteOwnerCancelCheck", { siteToken, sessionId });
}

export function siteCreateCheckEvidenceUpload(siteToken: string, payload: { sessionId: string; kind: "verdict_screenshot"; fileName: string; mimeType: "image/png" | "image/jpeg"; byteLength: number; sha256: string }) {
  return postOperation<SiteCheckEvidenceUpload>("siteCreateCheckEvidenceUpload", { siteToken, ...payload }, { noStore: true, timeoutMs: EVIDENCE_UPLOAD_TIMEOUT_MS });
}

export function siteUploadCheckEvidenceChunk(siteToken: string, uploadId: string, index: number, dataBase64: string) {
  return postOperation<{ ok: true; status: "ok" }>("siteUploadCheckEvidenceChunk", { siteToken, uploadId, index, dataBase64 }, { noStore: true, timeoutMs: EVIDENCE_UPLOAD_TIMEOUT_MS });
}

export function siteCompleteCheckEvidenceUpload(siteToken: string, uploadId: string) {
  return postOperation<{ ok: true; status: "ok" }>("siteCompleteCheckEvidenceUpload", { siteToken, uploadId }, { noStore: true, timeoutMs: EVIDENCE_UPLOAD_TIMEOUT_MS });
}

export function siteGetCheckEvidenceDownloadChunk(siteToken: string, sessionId: string, evidenceId: string, index: number) {
  return postOperation<{ ok: true; status: "ok"; index: number; dataBase64: string; chunkCount: number; evidence: SiteCheckEvidenceFile }>("siteGetCheckEvidenceDownloadChunk", { siteToken, sessionId, evidenceId, index });
}

export function siteCheckHistory(siteToken: string, payload: { query?: string; limit?: number; cursor?: string } = {}) {
  return postOperation<SiteCheckHistory>("siteCheckHistory", { siteToken, ...payload });
}

export function siteCheckDetails(siteToken: string, sessionId: string) {
  return postOperation<SiteCheckDetails>("siteCheckDetails", { siteToken, sessionId });
}

export function siteCheckPriorHistory(siteToken: string, suspectSteamId64: string) {
  return postOperation<SiteCheckPriorHistory>("siteCheckPriorHistory", { siteToken, suspectSteamId64 });
}

export function siteOwnerModeratorStats(siteToken: string, moderatorId: string) {
  return postOperation<SiteOwnerModeratorStats>("siteOwnerModeratorStats", { siteToken, moderatorId });
}

export function siteGetSecuritySettings(siteToken: string) {
  return postOperation<SiteSecuritySettings>("siteGetSecuritySettings", { siteToken });
}

export function siteTwoFactorSetupStart(siteToken: string) {
  return postOperation<SiteTwoFactorSetup>("siteTwoFactorSetupStart", { siteToken }, { noStore: true });
}

export function siteTwoFactorLoginSetupStart(twoFactorLoginSession: string) {
  return postOperation<SiteTwoFactorSetup>("siteTwoFactorSetupStart", { twoFactorLoginSession }, { noStore: true });
}

export function siteTwoFactorSetupConfirm(siteToken: string, code: string) {
  return postOperation<{ ok: true; status: "ok"; enabled: true }>("siteTwoFactorSetupConfirm", { siteToken, code }, { noStore: true });
}

export function siteTwoFactorLoginSetupConfirm(twoFactorLoginSession: string, code: string) {
  return postOperation<SiteAuthResult & { enabled: true }>("siteTwoFactorSetupConfirm", { twoFactorLoginSession, code }, { noStore: true });
}

export function siteCompleteTwoFactorLogin(twoFactorLoginSession: string, code: string) {
  return postOperation<SiteAuthResult>("siteCompleteTwoFactorLogin", { twoFactorLoginSession, code }, { noStore: true });
}

export function siteTwoFactorDisable(siteToken: string, code: string) {
  return postOperation<{ ok: true; status: "ok"; enabled: false }>("siteTwoFactorDisable", { siteToken, code }, { noStore: true });
}

export function siteCreateTestPurchase(siteToken: string, payload: { displayName: string; game: string; ownerDisplayName: string }) {
  return postOperation<SitePurchaseResult>("siteCreateTestPurchase", { siteToken, ...payload });
}

export function sitePaymentConfig(siteToken: string, language?: "ru" | "en") {
  return postOperation<SitePaymentConfig>("sitePaymentConfig", { siteToken, ...(language ? { language } : {}) }, { noStore: true });
}

export function siteCreatePayment(
  siteToken: string,
  payload: { planId: SubscriptionPlanId; method: "sbp" | "bank_card"; promoCode?: string; language?: "ru" | "en"; purpose?: "purchase" | "renewal" },
) {
  return postOperation<SitePaymentOrder>("siteCreatePayment", { siteToken, ...payload });
}

export function siteCancelPayment(siteToken: string, invoiceId?: string) {
  return postOperation<SitePaymentOrder>("siteCancelPayment", { siteToken, ...(invoiceId ? { invoiceId } : {}) });
}

export function siteCreateProject(siteToken: string, payload: { displayName: string; game: string; ownerDisplayName: string }) {
  return postOperation<SitePurchaseResult>("siteCreateProject", { siteToken, ...payload });
}

export function siteRequestProjectDeletion(siteToken: string, language: "ru" | "en", twoFactorCode?: string) {
  return postOperation<{ ok: boolean; status: string; expiresAt?: string }>("siteRequestProjectDeletion", { siteToken, language, ...(twoFactorCode ? { twoFactorCode } : {}) });
}

export function siteConfirmProjectDeletion(token: string, siteToken: string, twoFactorCode?: string) {
  return postOperation<{ ok: boolean; status: string }>("siteConfirmProjectDeletion", { token, siteToken, ...(twoFactorCode ? { twoFactorCode } : {}) });
}

export type SiteRevealedOwnerKey = {
  ok: true;
  status: "ok";
  scope: "owner";
  key: string;
  keyDisplayPrefix: string;
};

export function siteRevealOwnerKey(siteToken: string) {
  return postOperation<SiteRevealedOwnerKey>("siteRevealProjectKey", { siteToken, scope: "owner" });
}

export function sitePaymentStatus(siteToken: string, invoiceId?: string) {
  return postOperation<SitePaymentOrder>("sitePaymentStatus", { siteToken, ...(invoiceId ? { invoiceId } : {}) });
}

export function siteCreateModerator(siteToken: string, payload: { name: string; steamId64: string }) {
  return postOperation<{ ok: boolean; moderatorId: string; message: string }>("siteCreateModerator", { siteToken, ...payload });
}

export function siteUpdateProjectInfo(siteToken: string, payload: { displayName: string; game: string; ownerDisplayName: string }) {
  return postOperation<{ ok: boolean; status: string }>("siteUpdateProjectInfo", { siteToken, ...payload });
}

export function siteRenameModerator(siteToken: string, payload: { moderatorId: string; name: string }) {
  return postOperation<{ ok: boolean; status: string }>("siteRenameModerator", { siteToken, ...payload });
}

export function siteRemoveModerator(siteToken: string, moderatorId: string, twoFactorCode?: string) {
  return postOperation<{ ok: boolean; status: string }>("siteRemoveModerator", { siteToken, moderatorId, ...(twoFactorCode ? { twoFactorCode } : {}) });
}

export function siteOAuthStart(provider: "google" | "yandex", redirectUri: string, language: "ru" | "en") {
  return postOperation<SiteOAuthStartResult>("siteOAuthStart", { provider, redirectUri, language });
}

export function siteOAuthComplete(payload: {
  provider: "google" | "yandex";
  code: string;
  state: string;
  redirectUri: string;
}) {
  return postOperation<SiteAuthResult>("siteOAuthComplete", payload);
}

export function siteSteamStart(payload: { redirectUri: string; mode?: "link"; siteToken?: string; language: "ru" | "en" }) {
  return postOperation<SiteSteamStartResult>("siteSteamStart", payload);
}

export function siteSteamComplete(queryString: string) {
  return postOperation<SiteSteamCompleteResult>("siteSteamComplete", { queryString }, { allowPending: true, noStore: true });
}

export function siteAttachEmail(siteToken: string, email: string, language: "ru" | "en") {
  return postOperation<{ ok: boolean; status: string; user: SiteUser }>("siteAttachEmail", { siteToken, email, language });
}

export function siteUpdateLanguage(siteToken: string, language: "ru" | "en") {
  return postOperation<{ ok: boolean; status: string; user: SiteUser }>("siteUpdateLanguage", { siteToken, language });
}
